import {CSSProperties, ForwardedRef, Fragment, MutableRefObject, ReactNode, RefCallback, useMemo} from 'react'
import {StyledSelectPopover} from '@/components/commons/input-select/style'
import {Divider} from '@/components/ui/divider/Divider'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {XCloseIcon} from '@/components/ui/icon/Icon'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {Label} from '@/components/ui/label/Label'
import {SelectItem, SelectList, SelectTrigger, SelectGroupLabel} from '@/components/ui/select-atoms/SelectAtoms'
import {TKey, useTranslation} from '@/translations/i18n'
import {genericForwardRef, ObjectEntries, ObjectKeys} from '@/utilities/helpers'
import {SelectGroup, SelectProvider} from '@ariakit/react'

// Define mutually exclusive options interfaces
interface OptionsMapProps<T extends string = string> {
    optionsMap: Record<T, TKey>
    optionsEntries?: never
    optionsGroups?: never
}

interface OptionsEntriesProps<T extends string = string> {
    optionsEntries: [T, string][]
    optionsMap?: never
    optionsGroups?: never
}

interface OptionsGroupsProps<T extends string = string> {
    optionsGroups: {
        label?: TKey
        options: Record<T, TKey> | [T, string][]
    }[]
    optionsMap?: never
    optionsEntries?: never
}

// Define common props shared across all variants
interface CommonInputSelectProps<T extends string = string> {
    onChange: (value: T) => void
    label?: string
    placeholder?: string
    value?: T
    errorMessage?: string
    displayValue?: (label: string) => ReactNode
    onClear?: () => void
    isOptional?: boolean
    sameWidth?: boolean
    disabled?: boolean
    disabledOptions?: T[]
    reverseOrder?: boolean
    isPortal?: boolean
    portalRef?: RefCallback<HTMLElement> | MutableRefObject<HTMLElement | null>
    triggerClassName?: string
    hasItemCheckbox?: boolean
    style?: CSSProperties
}

export type InputSelectProps<T extends string = string> = (
    | OptionsMapProps<T>
    | OptionsEntriesProps<T>
    | OptionsGroupsProps<T>
) &
    CommonInputSelectProps<T>

export const InputSelect = genericForwardRef(
    <T extends string = string>(
        {
            label,
            value,
            placeholder,
            isOptional,
            onClear,
            displayValue,
            errorMessage,
            optionsMap,
            optionsEntries,
            optionsGroups,
            onChange,
            sameWidth,
            isPortal,
            portalRef,
            reverseOrder,
            disabled,
            disabledOptions,
            triggerClassName,
            hasItemCheckbox = true,
            style
        }: InputSelectProps<T>,
        ref: ForwardedRef<HTMLSelectElement>
    ) => {
        const {t} = useTranslation()

        const optionsValueLabelsMap = useMemo(() => {
            if (optionsMap) {
                return Object.fromEntries(ObjectEntries(optionsMap).map(([key, value]) => [key, t(value)]))
            } else if (optionsEntries) {
                return Object.fromEntries(optionsEntries)
            } else if (optionsGroups) {
                return Object.fromEntries(
                    optionsGroups.flatMap(group => {
                        if (Array.isArray(group.options)) {
                            // Handle array format
                            return group.options
                        } else {
                            // Handle record format
                            return ObjectEntries(group.options).map(([key, value]) => [key, t(value)])
                        }
                    })
                )
            }
            return {}
        }, [optionsMap, optionsEntries, optionsGroups, t])

        const optionsArray = useMemo(() => {
            if (optionsEntries) {
                return optionsEntries.map(([key]) => key)
            } else if (optionsMap) {
                return ObjectKeys(optionsMap)
            } else if (optionsGroups) {
                return optionsGroups.flatMap(group => {
                    if (Array.isArray(group.options)) {
                        return group.options.map(([key]) => key)
                    } else {
                        return ObjectKeys(group.options)
                    }
                })
            }
            return []
        }, [optionsEntries, optionsMap, optionsGroups])

        const orderedOptions = useMemo(
            () => (reverseOrder ? optionsArray.reverse() : optionsArray),
            [reverseOrder, optionsArray]
        )

        return (
            <SelectProvider value={value ?? ''} setValue={onChange}>
                <Flexbox direction="column" gap={1.5} fullWidth style={style}>
                    {label && (
                        <Label>
                            {label} {isOptional && <span className="description">({t('commons:optional')})</span>}
                        </Label>
                    )}
                    <SelectTrigger
                        ref={ref}
                        className={triggerClassName}
                        placeholder={placeholder ?? t('commons:select')}
                        value={value}
                        fullWidth
                        disabled={disabled}
                        hasError={!!errorMessage}
                        displayValue={value =>
                            typeof value == 'string' && (
                                <Flexbox justify="space-between" align="center" fullWidth gap={2}>
                                    {displayValue
                                        ? displayValue(optionsValueLabelsMap[value])
                                        : optionsValueLabelsMap[value]}
                                    {onClear && (
                                        <XCloseIcon
                                            role="button"
                                            onClick={e => {
                                                e.preventDefault()
                                                e.stopPropagation()
                                                onClear()
                                            }}
                                        />
                                    )}
                                </Flexbox>
                            )
                        }
                    />
                    {errorMessage && (
                        <Flexbox align="center" gap={errorMessage ? 1.5 : 0}>
                            <InputStatusIcon hasError={!!errorMessage} />
                            <InputHelpText error={errorMessage} />
                        </Flexbox>
                    )}
                    <StyledSelectPopover
                        portal={!!isPortal}
                        portalRef={portalRef}
                        sameWidth={sameWidth}
                        $hasGroups={!!optionsGroups}
                    >
                        <SelectList>
                            {optionsGroups
                                ? // Render grouped options
                                  optionsGroups.map((group, groupIndex) => (
                                      <Fragment key={groupIndex}>
                                          <SelectGroup>
                                              {group.label && <SelectGroupLabel>{t(group.label)}</SelectGroupLabel>}
                                              {Array.isArray(group.options)
                                                  ? // Handle array format
                                                    group.options.map(([option, label], index) => (
                                                        <SelectItem
                                                            disabled={disabledOptions?.includes(option)}
                                                            hasItemCheckbox
                                                            key={`${groupIndex}-${index}`}
                                                            value={option}
                                                        >
                                                            {label}
                                                        </SelectItem>
                                                    ))
                                                  : // Handle record format
                                                    ObjectEntries(group.options).map(([option, labelKey]) => (
                                                        <SelectItem
                                                            disabled={disabledOptions?.includes(option)}
                                                            key={option}
                                                            hasItemCheckbox
                                                            value={option}
                                                        >
                                                            {t(labelKey)}
                                                        </SelectItem>
                                                    ))}
                                          </SelectGroup>
                                          {groupIndex < optionsGroups.length - 1 && <Divider direction="horizontal" />}
                                      </Fragment>
                                  ))
                                : // Render flat options (existing behavior)
                                  orderedOptions.map((option, index) => (
                                      <SelectItem
                                          disabled={disabledOptions?.includes(option)}
                                          hasItemCheckbox={hasItemCheckbox}
                                          key={index}
                                          value={option}
                                      >
                                          {optionsValueLabelsMap[option]}
                                      </SelectItem>
                                  ))}
                        </SelectList>
                    </StyledSelectPopover>
                </Flexbox>
            </SelectProvider>
        )
    }
)
